"use client";

import { useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";

type DeviceSupport = {
  supported: boolean;
  note?: string;
  text?: string;
};

type MatrixRow = {
  id: number;
  feature: string;
  devices: Record<string, DeviceSupport>;
};

function DeviceSelect({
  label,
  value,
  columns,
  onChange,
  accent,
  className = "",
}: {
  label: string;
  value: string;
  columns: string[];
  onChange: (value: string) => void;
  accent?: boolean;
  className?: string;
}) {
  return (
    <label
      className={`relative flex min-h-[4.25rem] min-w-0 items-center px-2.5 py-3 ${
        accent ? "bg-primary text-white" : "bg-[#F2F3F6] text-[#12224F]"
      } ${className}`}
    >
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        className={`w-full appearance-none bg-transparent py-1 pr-5 text-center text-xs font-semibold leading-snug outline-none ${
          accent ? "text-white" : "text-[#12224F]"
        }`}
      >
        {columns.map(device => (
          <option key={device} value={device} className="text-[#12224F]">
            {device}
          </option>
        ))}
      </select>
      <ChevronDown
        aria-hidden
        className={`pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 ${
          accent ? "text-white/80" : "text-[#6B6F7C]"
        }`}
      />
    </label>
  );
}

function SupportCell({
  device,
  feature,
  data,
}: {
  device: string;
  feature: string;
  data: DeviceSupport;
}) {
  const label = `${device} ${data.supported ? "supports" : "does not support"} ${feature}`;

  if (data.text) {
    return <span className="block text-center text-[10px] leading-snug text-text-secondary">{data.text}</span>;
  }

  return data.supported ? (
    <Check aria-label={label} className="mx-auto text-[#12224F]" size={18} strokeWidth={2.5} />
  ) : (
    <X aria-label={label} className="mx-auto text-accent" size={18} strokeWidth={2.5} />
  );
}

export function DeviceMatrixMobile({
  columns,
  rows,
}: {
  columns: string[];
  rows: MatrixRow[];
}) {
  const [left, setLeft] = useState(columns[0] ?? "");
  const [right, setRight] = useState(columns[1] ?? columns[0] ?? "");

  return (
    <div className="max-w-full rounded-2xl border border-gray-200 bg-white">
      <div className="sticky top-[68px] z-20 grid grid-cols-2 rounded-t-2xl border-b border-gray-200">
        <DeviceSelect
          label="First player"
          value={left}
          columns={columns}
          onChange={setLeft}
          accent={left === "Android-Pickcel"}
          className="rounded-tl-2xl"
        />
        <DeviceSelect
          label="Second player"
          value={right}
          columns={columns}
          onChange={setRight}
          accent={right === "Android-Pickcel"}
          className="rounded-tr-2xl"
        />
      </div>

      <ul>
        {rows.map((row, index) => {
          const leftData = row.devices[left] ?? { supported: false };
          const rightData = row.devices[right] ?? { supported: false };
          const leftNote = leftData?.note?.trim() ?? "";
          const rightNote = rightData?.note?.trim() ?? "";

          return (
            <li
              key={row.id}
              className={`border-b border-gray-100 px-3 py-3 last:border-b-0 ${
                index % 2 === 0 ? "bg-white" : "bg-[#FAF9F6]"
              }`}
            >
              <p className="text-sm font-medium leading-snug text-text-primary">{row.feature}</p>
              {leftNote ? (
                <p className="mt-1 text-[11px] leading-snug text-text-secondary">
                  {left}: {leftNote}
                </p>
              ) : null}
              {rightNote && rightNote !== leftNote ? (
                <p className="mt-1 text-[11px] leading-snug text-text-secondary">
                  {right}: {rightNote}
                </p>
              ) : null}
              <div className="mt-2.5 flex gap-2">
                <div
                  className={`flex min-h-9 min-w-0 flex-1 items-center justify-center rounded-lg px-1.5 py-1.5 ${
                    left === "Android-Pickcel" ? "bg-primary/10" : "bg-gray-100"
                  }`}
                >
                  <SupportCell device={left} feature={row.feature} data={leftData} />
                </div>
                <div
                  className={`flex min-h-9 min-w-0 flex-1 items-center justify-center rounded-lg px-1.5 py-1.5 ${
                    right === "Android-Pickcel" ? "bg-primary/10" : "bg-gray-100"
                  }`}
                >
                  <SupportCell device={right} feature={row.feature} data={rightData} />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
